import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    InputLabel,
    FormControl,
    Tabs,
    Tab,
    Paper,
    makeStyles,
    Typography,
    Grid,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Divider
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    tabContent: {
        padding: theme.spacing(2),
        minHeight: "300px" // Ensure consistency
    },
    sectionTitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontWeight: "bold"
    }
}));

const professionalSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    email: Yup.string().email("Invalid email"),
    phone: Yup.string(),
});

const ProfessionalModal = ({ open, onClose, professionalId, reload }) => {
    const classes = useStyles();
    const [tab, setTab] = useState(0);
    const [professional, setProfessional] = useState({
        name: "",
        email: "",
        phone: "",
        subSpecialties: [], // List of ID
        workingHours: {},
        lunchBreak: {},
        workDays: []
    });
    const [subSpecialties, setSubSpecialties] = useState([]);
    const [groupedServices, setGroupedServices] = useState({});

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const { data } = await api.get("/subspecialties"); // Should return list with specialty included
                setSubSpecialties(data);

                // Group by Specialty Name
                const groups = data.reduce((acc, service) => {
                    const specName = service.specialty ? service.specialty.name : "Outros";
                    if (!acc[specName]) acc[specName] = [];
                    acc[specName].push(service);
                    return acc;
                }, {});
                setGroupedServices(groups);

            } catch (err) {
                toastError(err);
            }
        };
        fetchServices();
    }, [open]);

    useEffect(() => {
        const fetchProfessional = async () => {
            if (!professionalId) return;
            try {
                const { data } = await api.get(`/professionals/${professionalId}`);
                // Map associated specialties (actually ProfessionalSpecialties -> subSpecialtyId)
                // Need to check how backend returns this. 
                // Based on model, it returns 'specialties' which has 'subSpecialtyId'.
                const subIds = data.specialties ? data.specialties.map(s => s.subSpecialtyId).filter(id => id) : [];

                setProfessional({
                    ...data,
                    subSpecialties: subIds,
                    workDays: data.workDays || [],
                    workingHours: data.workingHours || {},
                    lunchBreak: data.lunchBreak || {} // { "mon": { start: "08:00", end: "18:00" } }
                });
            } catch (err) {
                toastError(err);
            }
        };

        if (open) {
            fetchProfessional();
        }
    }, [professionalId, open]);

    const handleClose = () => {
        onClose();
        setProfessional({ name: "", email: "", phone: "", subSpecialties: [], workDays: [], workingHours: {}, lunchBreak: {} });
        setTab(0);
    };

    const handleSaveProfessional = async (values) => {
        try {
            if (professionalId) {
                await api.put(`/professionals/${professionalId}`, values);
            } else {
                await api.post("/professionals", values);
            }
            toast.success(i18n.t("professionals.toasts.success"));
            reload();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const daysOfWeek = [
        { key: "mon", label: "Segunda-feira" },
        { key: "tue", label: "Terça-feira" },
        { key: "wed", label: "Quarta-feira" },
        { key: "thu", label: "Quinta-feira" },
        { key: "fri", label: "Sexta-feira" },
        { key: "sat", label: "Sábado" },
        { key: "sun", label: "Domingo" }
    ];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {professionalId
                    ? i18n.t("professionals.modal.edit")
                    : i18n.t("professionals.modal.add")}
            </DialogTitle>

            <Formik
                initialValues={professional}
                enableReinitialize={true}
                validationSchema={professionalSchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSaveProfessional(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, isSubmitting, isValid, values, setFieldValue }) => (
                    <Form>
                        <Paper square>
                            <Tabs value={tab} indicatorColor="primary" textColor="primary" onChange={handleTabChange} variant="fullWidth">
                                <Tab label="Geral" />
                                <Tab label="Serviços" />
                                <Tab label="Disponibilidades" />
                            </Tabs>
                        </Paper>

                        <DialogContent dividers className={classes.tabContent}>
                            {tab === 0 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("professionals.modal.form.name")}
                                            autoFocus
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("professionals.modal.form.email")}
                                            name="email"
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("professionals.modal.form.phone")}
                                            name="phone"
                                            error={touched.phone && Boolean(errors.phone)}
                                            helperText={touched.phone && errors.phone}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {tab === 1 && (
                                <div style={{ height: "400px", overflowY: "auto" }}>
                                    {Object.keys(groupedServices).map(group => (
                                        <div key={group}>
                                            <Typography variant="subtitle1" className={classes.sectionTitle}>{group}</Typography>
                                            <Divider />
                                            <Grid container spacing={1}>
                                                {groupedServices[group].map(service => (
                                                    <Grid item xs={12} sm={6} md={4} key={service.id}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={values.subSpecialties.includes(service.id)}
                                                                    onChange={(e) => {
                                                                        const current = values.subSpecialties;
                                                                        if (e.target.checked) {
                                                                            setFieldValue("subSpecialties", [...current, service.id]);
                                                                        } else {
                                                                            setFieldValue("subSpecialties", current.filter(id => id !== service.id));
                                                                        }
                                                                    }}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={service.name}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {tab === 2 && (
                                <div style={{ height: "400px", overflowY: "auto" }}>
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                                        Defina os horários de trabalho e almoço para cada dia.
                                    </Typography>
                                    {daysOfWeek.map(day => (
                                        <Paper key={day.key} variant="outlined" style={{ padding: 10, marginBottom: 10 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.workDays.includes(day.key)}
                                                        onChange={(e) => {
                                                            const current = values.workDays;
                                                            if (e.target.checked) {
                                                                setFieldValue("workDays", [...current, day.key]);
                                                                // Set default hours if added
                                                                setFieldValue(`workingHours.${day.key}`, { start: "08:00", end: "18:00" });
                                                                setFieldValue(`lunchBreak.${day.key}`, { start: "12:00", end: "13:00" });
                                                            } else {
                                                                setFieldValue("workDays", current.filter(d => d !== day.key));
                                                            }
                                                        }}
                                                        color="primary"
                                                    />
                                                }
                                                label={day.label}
                                            />
                                            {values.workDays.includes(day.key) && (
                                                <Grid container spacing={2} style={{ marginLeft: 20 }}>
                                                    <Grid item xs={12} sm={3}>
                                                        <InputLabel>Início Trabalho</InputLabel>
                                                        <Field
                                                            as={TextField}
                                                            type="time"
                                                            name={`workingHours.${day.key}.start`}
                                                            variant="standard"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <InputLabel>Fim Trabalho</InputLabel>
                                                        <Field
                                                            as={TextField}
                                                            type="time"
                                                            name={`workingHours.${day.key}.end`}
                                                            variant="standard"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <InputLabel>Início Almoço</InputLabel>
                                                        <Field
                                                            as={TextField}
                                                            type="time"
                                                            name={`lunchBreak.${day.key}.start`}
                                                            variant="standard"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <InputLabel>Fim Almoço</InputLabel>
                                                        <Field
                                                            as={TextField}
                                                            type="time"
                                                            name={`lunchBreak.${day.key}.end`}
                                                            variant="standard"
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Paper>
                                    ))}
                                </div>
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                {i18n.t("global.buttons.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting || !isValid}
                                variant="contained"
                                className={isSubmitting ? "circularProgress" : ""}
                            >
                                {i18n.t("global.buttons.add")}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className="buttonProgress"
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default ProfessionalModal;
